// Executive Market Read — weekly briefing card shown above the KPI strip in the
// Market View. Renders the briefing returned by `useSignalData.selectedBriefing`.
// All shaping is defensive: arrays may be missing, items can be strings or
// objects with mixed casing (camelCase or PascalCase). No LLM calls.

import React, { useState } from "react";
import EvidenceChips from "./EvidenceChips";
import { readClaimEvidence, readTopSignalEvidence, hasAnyEvidenceChip, pickClaimSources } from "../../lib/signals/evidence";

const pickStr = (...vals) => {
    for (const v of vals) {
        if (typeof v === "string" && v.trim()) return v.trim();
    }
    return null;
};

const renderListItem = (item) => {
    if (item == null) return null;
    if (typeof item === "string") return item.trim() || null;
    if (typeof item !== "object") return String(item);
    // generic prose-style object: pick the most useful field
    return pickStr(
        item.text,
        item.headline,
        item.title,
        item.summary,
        item.implication,
        item.label,
        item.point,
        item.message
    );
};

const renderTopSignal = (item) => {
    if (item == null) return null;
    if (typeof item === "string") return { name: item, meta: null, reason: null };
    if (typeof item !== "object") return { name: String(item), meta: null, reason: null };
    const name = pickStr(item.signalName, item.SignalName, item.name, item.title);
    const score = item.score != null ? item.score : (item.SignalScore != null ? item.SignalScore : item.signalScoreNumeric);
    const id = pickStr(item.signalID, item.SignalID, item.signalId);
    const reason = pickStr(item.reason, item.summary, item.note, item.framing, item.shortNarrative);
    const meta = [score != null ? `score ${score}` : null, id].filter(Boolean).join(" · ") || null;
    if (!name && !reason) return null;
    return { name: name || "Untitled signal", meta, reason };
};

const renderRegItem = (item) => {
    if (item == null) return null;
    if (typeof item === "string") return { title: item, meta: null, summary: null };
    if (typeof item !== "object") return { title: String(item), meta: null, summary: null };
    const title = pickStr(item.title, item.headline, item.issue, item.SignalName, item.signalName, item.name);
    const jurisdiction = pickStr(item.jurisdiction, item.region, item.country, item.market);
    const summary = pickStr(item.summary, item.reason, item.note, item.framing, item.shortNarrative, item.bigPicture);
    const meta = jurisdiction || null;
    if (!title && !summary) return null;
    return { title: title || "Regulatory item", meta, summary };
};

const Section = ({ title, children, testId }) => (
    <div className="mt-5" data-testid={testId}>
        <div className="eyebrow mb-2">{title}</div>
        {children}
    </div>
);

const Bullets = ({ items, testId, evidenceList, evidenceTestIdScope }) => {
    const list = (items || []).map(renderListItem).filter(Boolean);
    if (!list.length) return null;
    return (
        <ul className="space-y-1.5 text-sm leading-relaxed text-[hsl(var(--ink-soft))]" data-testid={testId}>
            {list.map((t, i) => {
                const claimSrc = Array.isArray(evidenceList) ? evidenceList[i] : null;
                const claim = claimSrc ? readClaimEvidence(claimSrc) : null;
                const showChips = claim && hasAnyEvidenceChip(claim.evidence);
                return (
                    <li key={i} className="flex gap-2.5">
                        <span aria-hidden className="text-[hsl(var(--accent-ink))] flex-none mt-[7px] w-1 h-1 rounded-full bg-[hsl(var(--accent))]" />
                        <div className="min-w-0 flex-1">
                            <span>{t}</span>
                            {showChips && (
                                <EvidenceChips
                                    evidence={claim.evidence}
                                    sources={pickClaimSources(claim.evidence)}
                                    testIdScope={`${evidenceTestIdScope || "emr-evidence"}-${i}`}
                                    dense
                                />
                            )}
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

// Resolve the structured-evidence arrays for the new fields with documented
// fallbacks. `evidenceLayer.{bigPicture,executiveSummary,...}` is preferred,
// then the lowerCamel aliases shipped at the top of the briefing.
const resolveEvidenceSources = (briefing) => {
    const el = briefing && briefing.evidenceLayer ? briefing.evidenceLayer : null;
    return {
        bigPicture: (el && el.bigPicture) || briefing.bigPictureEvidence || null,
        executiveSummary: (el && el.executiveSummary) || briefing.executiveSummaryEvidence || null,
        keyTakeaways: (el && Array.isArray(el.keyTakeaways) && el.keyTakeaways)
            || (Array.isArray(briefing.keyTakeawaysStructured) ? briefing.keyTakeawaysStructured : null),
        marketStructureImplications: (el && Array.isArray(el.marketStructureImplications) && el.marketStructureImplications)
            || (Array.isArray(briefing.marketStructureImplicationsStructured) ? briefing.marketStructureImplicationsStructured : null),
        watchItems: (el && Array.isArray(el.watchItems) && el.watchItems)
            || (Array.isArray(briefing.watchItemsStructured) ? briefing.watchItemsStructured : null),
    };
};

const ExecutiveMarketRead = ({ briefing, referencedSignals, onSelectTrack }) => {
    const [expanded, setExpanded] = useState(false);
    if (!briefing) return null;

    const headlineShort = pickStr(briefing.headlineShort, briefing.headline);
    const bigPictureShort = pickStr(briefing.bigPictureShort, briefing.bigPicture);
    const fullHeadline = pickStr(briefing.headline, briefing.headlineShort);
    const fullBigPicture = pickStr(briefing.bigPicture, briefing.bigPictureShort);
    const execSummary = pickStr(briefing.executiveSummary);
    const dateRange = pickStr(briefing.dateRange);

    const takeaways = Array.isArray(briefing.keyTakeaways) ? briefing.keyTakeaways.map(renderListItem).filter(Boolean) : [];
    const compactTakeaways = takeaways.slice(0, 3);
    const implications = Array.isArray(briefing.marketStructureImplications) ? briefing.marketStructureImplications.map(renderListItem).filter(Boolean) : [];
    const watch = Array.isArray(briefing.watchItems) ? briefing.watchItems.map(renderListItem).filter(Boolean) : [];
    const rawTopSignals = Array.isArray(briefing.topSignals) ? briefing.topSignals : [];
    const topSignals = rawTopSignals.map(renderTopSignal).map((rendered, i) => rendered ? { ...rendered, _raw: rawTopSignals[i] } : null).filter(Boolean);
    const regulatory = Array.isArray(briefing.regulatoryWatch) ? briefing.regulatoryWatch.map(renderRegItem).filter(Boolean) : [];

    // Structured evidence sources for the new field set. Each may be null when
    // older weeks don't ship the new fields — `EvidenceChips` then renders nothing.
    const ev = resolveEvidenceSources(briefing);
    const bigPictureClaim = ev.bigPicture ? readClaimEvidence(ev.bigPicture) : null;
    const execSummaryClaim = ev.executiveSummary ? readClaimEvidence(ev.executiveSummary) : null;

    if (!headlineShort && !bigPictureShort && !takeaways.length && !execSummary && !implications.length) {
        return null;
    }

    return (
        <section className="card-flat p-6" data-testid="executive-market-read">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className="eyebrow">Executive market read</span>
                        {dateRange && <span className="text-[11px] text-[hsl(var(--mute))] num">{dateRange}</span>}
                    </div>
                    {headlineShort && (
                        <h2 className="display text-2xl mt-2 leading-snug text-[hsl(var(--ink))]" data-testid="emr-headline">
                            {headlineShort}
                        </h2>
                    )}
                    {execSummary ? (
                        <>
                            <p className="text-sm leading-relaxed text-[hsl(var(--ink-soft))] mt-3 whitespace-pre-line" data-testid="emr-exec-summary-lead">
                                {execSummary}
                            </p>
                            {execSummaryClaim && hasAnyEvidenceChip(execSummaryClaim.evidence) && (
                                <EvidenceChips
                                    evidence={execSummaryClaim.evidence}
                                    sources={pickClaimSources(execSummaryClaim.evidence)}
                                    testIdScope="emr-evidence-exec-summary"
                                    maxChips={3}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {bigPictureShort && (
                                <p className="text-sm leading-relaxed text-[hsl(var(--ink-soft))] mt-2" data-testid="emr-big-picture">
                                    {bigPictureShort}
                                </p>
                            )}
                            {bigPictureClaim && hasAnyEvidenceChip(bigPictureClaim.evidence) && (
                                <EvidenceChips
                                    evidence={bigPictureClaim.evidence}
                                    sources={pickClaimSources(bigPictureClaim.evidence)}
                                    testIdScope="emr-evidence-big-picture"
                                    maxChips={3}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>

            {compactTakeaways.length > 0 && !expanded && (
                <div className="mt-5">
                    <div className="eyebrow mb-2">Key takeaways</div>
                    <ol className="space-y-3 text-sm leading-relaxed" data-testid="emr-takeaways-compact">
                        {compactTakeaways.map((t, i) => {
                            const claimSrc = Array.isArray(ev.keyTakeaways) ? ev.keyTakeaways[i] : null;
                            const claim = claimSrc ? readClaimEvidence(claimSrc) : null;
                            const showChips = claim && hasAnyEvidenceChip(claim.evidence);
                            return (
                                <li key={i} className="flex gap-3">
                                    <span className="num text-[11px] font-semibold text-[hsl(var(--mute))] pt-0.5 w-6 flex-none">{String(i + 1).padStart(2, "0")}</span>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[hsl(var(--ink-soft))]">{t}</p>
                                        {showChips && (
                                            <div className="mt-1.5">
                                                <EvidenceChips
                                                    evidence={claim.evidence}
                                                    sources={pickClaimSources(claim.evidence)}
                                                    testIdScope={`emr-evidence-compact-takeaway-${i}`}
                                                    dense
                                                />
                                            </div>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            )}

            {expanded && (
                <div className="mt-2" data-testid="emr-expanded">
                    {takeaways.length > 0 && (
                        <Section title={`Key takeaways · ${takeaways.length}`} testId="emr-section-takeaways-all">
                            <ol className="space-y-3 text-sm leading-relaxed">
                                {takeaways.map((t, i) => {
                                    const claimSrc = Array.isArray(ev.keyTakeaways) ? ev.keyTakeaways[i] : null;
                                    const claim = claimSrc ? readClaimEvidence(claimSrc) : null;
                                    const showChips = claim && hasAnyEvidenceChip(claim.evidence);
                                    return (
                                        <li key={i} className="flex gap-3">
                                            <span className="num text-[11px] font-semibold text-[hsl(var(--mute))] pt-0.5 w-6 flex-none">{String(i + 1).padStart(2, "0")}</span>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[hsl(var(--ink-soft))]">{t}</p>
                                                {showChips && (
                                                    <div className="mt-1.5">
                                                        <EvidenceChips
                                                            evidence={claim.evidence}
                                                            sources={pickClaimSources(claim.evidence)}
                                                            testIdScope={`emr-evidence-takeaway-${i}`}
                                                            dense
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </Section>
                    )}
                    {implications.length > 0 && (
                        <Section title="Market structure implications" testId="emr-section-implications">
                            <Bullets items={implications} testId="emr-implications-list" evidenceList={ev.marketStructureImplications} evidenceTestIdScope="emr-evidence-implication" />
                        </Section>
                    )}
                    {watch.length > 0 && (
                        <Section title="Watch items" testId="emr-section-watch">
                            <Bullets items={watch} testId="emr-watch-list" evidenceList={ev.watchItems} evidenceTestIdScope="emr-evidence-watch" />
                        </Section>
                    )}
                    {topSignals.length > 0 && (
                        <Section title={`Top signals · ${topSignals.length}`} testId="emr-section-top-signals">
                            <ul className="space-y-2.5">
                                {topSignals.map((s, i) => {
                                    const tsEvidence = readTopSignalEvidence(s._raw);
                                    return (
                                        <li key={i} className="text-sm leading-snug" data-testid={`emr-top-signal-${i}`}>
                                            <div className="font-medium text-[hsl(var(--ink))]">{s.name}</div>
                                            {s.meta && <div className="text-[11px] text-[hsl(var(--mute))] num mt-0.5">{s.meta}</div>}
                                            {s.reason && <div className="text-xs text-[hsl(var(--ink-soft))] mt-1 leading-relaxed">{s.reason}</div>}
                                            {hasAnyEvidenceChip(tsEvidence) && (
                                                <EvidenceChips
                                                    evidence={tsEvidence}
                                                    testIdScope={`emr-evidence-top-signal-${i}`}
                                                    maxChips={3}
                                                    dense
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </Section>
                    )}
                    {regulatory.length > 0 && (
                        <Section title={`Regulatory watch · ${regulatory.length}`} testId="emr-section-regulatory">
                            <ul className="space-y-2.5">
                                {regulatory.map((r, i) => (
                                    <li key={i} className="text-sm leading-snug" data-testid={`emr-reg-${i}`}>
                                        <div className="font-medium text-[hsl(var(--ink))]">{r.title}</div>
                                        {r.meta && <div className="text-[11px] text-[hsl(var(--mute))] mt-0.5 uppercase tracking-wider">{r.meta}</div>}
                                        {r.summary && <div className="text-xs text-[hsl(var(--ink-soft))] mt-1 leading-relaxed">{r.summary}</div>}
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}
                    {(fullBigPicture || fullHeadline) && (fullBigPicture !== bigPictureShort || fullHeadline !== headlineShort) && (
                        <Section title="Full market read" testId="emr-section-full-read">
                            {fullHeadline && fullHeadline !== headlineShort && (
                                <p className="text-sm italic text-[hsl(var(--ink-soft))] leading-relaxed">{fullHeadline}</p>
                            )}
                            {fullBigPicture && (
                                <p className={`text-sm text-[hsl(var(--ink-soft))] leading-relaxed whitespace-pre-line ${fullHeadline && fullHeadline !== headlineShort ? "mt-3" : ""}`}>{fullBigPicture}</p>
                            )}
                        </Section>
                    )}
                </div>
            )}

            {(execSummary || implications.length > 0 || watch.length > 0 || topSignals.length > 0 || regulatory.length > 0 || takeaways.length > compactTakeaways.length) && (
                <div className="mt-5 pt-4 border-t border-[hsl(var(--line))] flex items-center justify-between">
                    <button
                        type="button"
                        data-testid="emr-toggle"
                        onClick={() => setExpanded((v) => !v)}
                        className="text-xs font-medium text-[hsl(var(--accent-ink))] hover:underline"
                        aria-expanded={expanded}
                    >
                        {expanded ? "Hide full briefing" : "Show full briefing"} →
                    </button>
                    <div className="text-[11px] text-[hsl(var(--mute))] flex gap-3 num">
                        {takeaways.length > 0 && <span>{takeaways.length} takeaways</span>}
                        {topSignals.length > 0 && <span>{topSignals.length} top signals</span>}
                        {regulatory.length > 0 && <span>{regulatory.length} regulatory</span>}
                    </div>
                </div>
            )}

            {Array.isArray(referencedSignals) && referencedSignals.some((r) => r.trackId) && (
                <div className="mt-4 pt-4 border-t border-[hsl(var(--line))]" data-testid="emr-referenced-signals">
                    <div className="eyebrow mb-2">Signals referenced in this read</div>
                    <div className="flex flex-wrap gap-1.5">
                        {/* Drop references that can't be resolved to a track in
                            the current governed payload. A disabled chip still
                            reads as a broken link to users, so unmatched items
                            are filtered out entirely rather than rendered in a
                            muted state. The matching pipeline in
                            `briefingReferencedItems` already covers explicit
                            TrackID/SignalID, exact + normalised name lookups
                            across track fields and historical weekly framings;
                            anything still without a `trackId` truly has no
                            corresponding signal in the current registry. */}
                        {referencedSignals.filter((r) => !!(r.trackId && onSelectTrack)).slice(0, 8).map((r, i) => {
                            const tone = r.kind === "regulatory"
                                ? "border-[hsl(var(--regulatory))] bg-[hsl(var(--regulatory-soft))] text-[hsl(var(--regulatory))]"
                                : "border-[hsl(var(--accent))] bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent-ink))]";
                            return (
                                <button
                                    key={`${r.signalID || r.name}-${i}`}
                                    type="button"
                                    onClick={() => onSelectTrack(r.trackId)}
                                    title={`Select "${r.name}" in the Market Signals board`}
                                    data-testid={`emr-ref-${i}`}
                                    className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors hover:opacity-80 cursor-pointer ${tone}`}
                                >
                                    <span aria-hidden className="mr-1">{r.kind === "regulatory" ? "§" : "★"}</span>
                                    {r.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
};

export default ExecutiveMarketRead;
